<script setup lang="ts">
import { BubbleMenu, type Editor } from "@tiptap/vue-3";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import type { BubbleMenuPluginProps } from "@tiptap/extension-bubble-menu";
import { isTextSelection } from "@tiptap/core";
import type { ObjectId } from "~/composables/charon";

const { t } = useI18n()

const props = defineProps<{
  editor: Editor
}>()

const extensions = computed(() =>
  props.editor
    .extensionManager
    .extensions
    .map(extension => extension.name)
)

const fileViewer = ref({
  open: false,
  mode: 'single' as 'single' | 'multi'
})

function selectFiles(fileIds: ObjectId[]) {
  if (fileViewer.value.mode == 'single') {
    props.editor.commands.updateCharonFile({ fileId: fileIds[0] })
    return
  }

  props.editor.commands.insertContentAt(
    props.editor.state.selection.$head.pos,
    fileIds.map(fileId => ({
      type: 'charon-file',
      attrs: { fileId: fileId }
    }))
  )
}

const shouldShow: BubbleMenuPluginProps["shouldShow"] = ({ view, state }) => {
  const isTextBlock = isTextSelection(state.selection)
  const isGallery = props.editor.isActive("gallery")
  const hasEditorFocus = view.hasFocus()
  return hasEditorFocus && isGallery && !isTextBlock && props.editor.isEditable
}
</script>

<template>
  <BubbleMenu
    :editor="props.editor"
    :tippy-options="{ duration: 100, zIndex: 5 }"
    :should-show="shouldShow"
    pluginKey="bubble-gallery"
    class="bg-secondary-highlighted w-min rounded flex flex-row shadow"
  >
    <button
      v-if="extensions.includes('gallery') && extensions.includes('charon-file')"
      :title="t('jc.component.editor.button.insertFile.title')"
      @click="fileViewer = { open: true, mode: 'multi' }"
      class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
    >
      <font-awesome-icon
        icon="file-plus"
        fixed-width
      />
    </button>
    <button
      v-if="extensions.includes('gallery') && extensions.includes('charon-file')"
      :title="t('jc.component.editor.button.updateFile.title')"
      @click="fileViewer = { open: true, mode: 'single' }"
      class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
    >
      <font-awesome-icon
        icon="file-pen"
        fixed-width
      />
    </button>
    <button
      v-if="extensions.includes('gallery') && extensions.includes('charon-file')"
      :disabled="!props.editor.isActive('charon-file')"
      :title="t('jc.component.editor.button.removeFile.title')"
      @click="props.editor.commands.deleteSelection()"
      class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
    >
      <font-awesome-icon
        icon="file-minus"
        fixed-width
      />
    </button>
  </BubbleMenu>

  <FileViewer
    v-if="fileViewer.open"
    :select="fileViewer.mode"
    @click:select="selectFiles"
    @click:close="() => fileViewer.open = false"
  />
</template>

<style scoped lang="postcss">

</style>
